import { useState, useMemo, useEffect } from "react";
import { QrCode, X, Copy, Check, Printer, Shield, ExternalLink, MapPin } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "react-toastify";
import { SelectDropdown } from "../ui";
import styles from "./PropertyQrPosterModal.module.css";

const ENTRY_TYPES = [
  { value: "ALL", label: "General Standee (Visitor Chooses)" },
  { value: "PEDESTRIAN", label: "Pedestrian / Guest Walk-in" },
  { value: "VEHICLE", label: "Vehicle / Parking Entry" },
  { value: "DELIVERY", label: "Delivery / Courier Drop-off" },
  { value: "VENDOR", label: "Vendor / Maintenance Staff" },
];

export default function PropertyQrPosterModal({
  isOpen,
  onClose,
  societyId,
  propertyName = "",
  gates = [],
  initialGateId = "1",
  initialType = "ALL",
}) {
  const [resolvedGates, setResolvedGates] = useState(gates || []);
  const [selectedGate, setSelectedGate] = useState(() => {
    if (initialGateId && String(initialGateId).trim()) return String(initialGateId);
    if (gates && gates.length > 0) return String(gates[0].id || gates[0].gate_id || "1");
    return "1";
  });
  const [selectedType, setSelectedType] = useState(() => String(initialType || "ALL"));
  const [copied, setCopied] = useState(false);

  const [resolvedPropertyName, setResolvedPropertyName] = useState(
    propertyName && propertyName !== "AsmitA Operations" ? propertyName : ""
  );

  useEffect(() => {
    if (initialGateId && String(initialGateId).trim()) {
      setSelectedGate(String(initialGateId));
    }
  }, [initialGateId]);

  useEffect(() => {
    if (propertyName && propertyName !== "AsmitA Operations") {
      setResolvedPropertyName(propertyName);
    }
  }, [propertyName]);

  useEffect(() => {
    if (gates && gates.length > 0) {
      setResolvedGates(gates);
    }
  }, [gates]);

  // Keep selectedGate valid whenever resolvedGates update
  useEffect(() => {
    if (resolvedGates && resolvedGates.length > 0) {
      const match = resolvedGates.some(
        (g) => String(g.id || g.gate_id) === String(selectedGate)
      );
      if (!match) {
        // Fall back to initialGateId if present, else first gate
        const hasInitial = initialGateId && resolvedGates.some(
          (g) => String(g.id || g.gate_id) === String(initialGateId)
        );
        const fallback = hasInitial
          ? String(initialGateId)
          : String(resolvedGates[0].id || resolvedGates[0].gate_id);
        setSelectedGate(fallback);
      }
    }
  }, [resolvedGates, initialGateId, selectedGate]);

  // Fetch real property info if missing
  useEffect(() => {
    if (!isOpen || !societyId) return;
    const fetchPropertyInfo = async () => {
      try {
        const res = await fetch(`/api/public/visitor/departments?society_id=${societyId}`);
        const data = await res.json();
        if (data.success && (data.society_name || data.property_name)) {
          setResolvedPropertyName(data.society_name || data.property_name);
        }
      } catch (err) {
        console.error("Failed to load property name for QR modal:", err);
      }
    };

    if (!resolvedPropertyName) {
      fetchPropertyInfo();
    }
  }, [isOpen, societyId, resolvedPropertyName]);

  // Fetch real gates if empty
  useEffect(() => {
    if (!isOpen || !societyId) return;
    const fetchGates = async () => {
      try {
        const res = await fetch(`/api/societies/${societyId}/gates`);
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setResolvedGates(data);
        } else if (data.data && Array.isArray(data.data)) {
          setResolvedGates(data.data);
        }
      } catch (err) {
        console.error("Failed to fetch gates for QR modal:", err);
      }
    };

    if (!resolvedGates || resolvedGates.length === 0) {
      fetchGates();
    }
  }, [isOpen, societyId, resolvedGates]);

  // Compute the live target check-in URL
  const targetUrl = useMemo(() => {
    const origin = typeof window !== "undefined" ? window.location.origin : "https://admin.myasmita.com";
    const params = new URLSearchParams();
    params.set("societyId", String(societyId || 114));
    params.set("gate", selectedGate);
    if (selectedType && selectedType !== "ALL") {
      params.set("type", selectedType);
    }
    return `${origin}/visitor/checkin?${params.toString()}`;
  }, [societyId, selectedGate, selectedType]);

  // Fallback QR Code image URL (high-res 400x400)
  const qrImageUrl = useMemo(() => {
    return `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(
      targetUrl
    )}&margin=10`;
  }, [targetUrl]);

  const selectedGateName = useMemo(() => {
    const found = resolvedGates.find(
      (g) => String(g.id || g.gate_id) === String(selectedGate)
    );
    return found ? found.name || found.gate_name : `Gate ${selectedGate}`;
  }, [resolvedGates, selectedGate]);

  const selectedTypeObj = useMemo(() => {
    return ENTRY_TYPES.find((t) => t.value === selectedType) || ENTRY_TYPES[0];
  }, [selectedType]);

  const gateOptions = useMemo(() => {
    if (resolvedGates && resolvedGates.length > 0) {
      return resolvedGates.map((g) => ({
        value: String(g.id || g.gate_id),
        label: `${g.name || g.gate_name} (Gate ${g.id || g.gate_id})`,
      }));
    }
    return [
      { value: "1", label: "Gate 1: Main Pedestrian Gate" },
      { value: "2", label: "Gate 2: Parking & Freight Gate" },
      { value: "3", label: "Gate 3: Service Entrance" },
    ];
  }, [resolvedGates]);

  const categoryOptions = useMemo(() => ENTRY_TYPES, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(targetUrl);
    setCopied(true);
    toast.success("Check-in URL copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePrintPoster = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      toast.warn("Please allow popups to print the standee poster.");
      return;
    }

    const origin = typeof window !== "undefined" ? window.location.origin : "https://admin.myasmita.com";
    let qrDataUrl = "";
    const canvas = document.getElementById("standee-qr-canvas");
    if (canvas) {
      try {
        qrDataUrl = canvas.toDataURL("image/png");
      } catch (err) {
        console.error("Canvas export failed:", err);
      }
    }
    if (!qrDataUrl) {
      qrDataUrl = qrImageUrl;
    }

    const typeTitle =
      selectedType === "ALL"
        ? "SELF CHECK-IN PASS"
        : `${selectedTypeObj.label.toUpperCase()} PASS`;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>${resolvedPropertyName || "Property Access"} - ${selectedGateName} QR Standee</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@600;700;800;900&family=Poppins:wght@400;500;600;700&display=swap');
            
            @page {
              size: A4 portrait;
              margin: 12mm;
            }
            
            * {
              box-sizing: border-box;
            }
            
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              min-height: 100vh;
              background: #f8fafc;
              font-family: 'Poppins', sans-serif;
              color: #0f172a;
              display: flex;
              justify-content: center;
              align-items: center;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .standee-container {
              width: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
              padding: 24px;
            }

            .standee-card {
              width: 100%;
              max-width: 530px;
              margin: 0 auto;
              border: 3.5px solid #27306f;
              border-radius: 28px;
              padding: 34px 30px 24px;
              text-align: center;
              background: #ffffff;
              box-shadow: 0 16px 40px rgba(39, 48, 111, 0.08);
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .brand-badge {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
              background: rgba(39, 48, 111, 0.08);
              color: #27306f;
              border: 1.5px solid #27306f;
              padding: 6px 18px;
              border-radius: 999px;
              font-family: 'Montserrat', sans-serif;
              font-weight: 800;
              font-size: 12px;
              letter-spacing: 1px;
              text-transform: uppercase;
              margin-bottom: 14px;
            }

            .property-title {
              font-family: 'Montserrat', sans-serif;
              font-size: 26px;
              font-weight: 800;
              color: #0f172a;
              margin: 0 0 6px 0;
              line-height: 1.25;
            }

            .gate-info {
              font-size: 14.5px;
              font-weight: 600;
              color: #64748b;
              margin: 0 0 14px 0;
            }

            .category-tag {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              background: #27306f;
              color: #ffffff;
              padding: 7px 22px;
              border-radius: 999px;
              font-family: 'Montserrat', sans-serif;
              font-size: 13px;
              font-weight: 700;
              letter-spacing: 0.5px;
              margin-bottom: 20px;
            }

            .qr-frame {
              position: relative;
              background: #ffffff;
              border: 2px solid #e2e8f0;
              padding: 16px;
              border-radius: 22px;
              display: inline-flex;
              justify-content: center;
              align-items: center;
              margin-bottom: 12px;
              box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
            }

            .qr-img {
              width: 260px;
              height: 260px;
              display: block;
              border-radius: 10px;
            }

            .qr-center-badge {
              position: absolute;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              width: 54px;
              height: 54px;
              background: #ffffff;
              border-radius: 12px;
              padding: 5px;
              box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
              display: flex;
              align-items: center;
              justify-content: center;
              pointer-events: none;
            }

            .qr-center-logo {
              max-width: 100%;
              max-height: 100%;
              object-fit: contain;
            }

            .scan-hint {
              font-size: 12px;
              font-weight: 600;
              color: #475569;
              margin: 0 0 16px 0;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 6px;
            }

            .divider {
              width: 100%;
              height: 1.5px;
              background: #e2e8f0;
              margin-bottom: 18px;
            }

            .steps-grid {
              display: grid;
              grid-template-columns: repeat(3, 1fr);
              gap: 12px;
              width: 100%;
              text-align: center;
              margin-bottom: 20px;
            }

            .step-item {
              display: flex;
              flex-direction: column;
              align-items: center;
            }

            .step-num {
              width: 28px;
              height: 28px;
              line-height: 28px;
              background: #27306f;
              color: #ffffff;
              border-radius: 50%;
              font-family: 'Montserrat', sans-serif;
              font-weight: 800;
              font-size: 12px;
              margin-bottom: 6px;
            }

            .step-title {
              font-family: 'Montserrat', sans-serif;
              font-size: 11.5px;
              font-weight: 700;
              color: #1e293b;
              margin-bottom: 2px;
            }

            .step-text {
              font-size: 10.5px;
              color: #64748b;
              font-weight: 500;
              line-height: 1.3;
            }

            .footer-note {
              font-size: 9.5px;
              color: #94a3b8;
              font-weight: 600;
              letter-spacing: 0.8px;
              text-transform: uppercase;
              border-top: 1px solid #f1f5f9;
              width: 100%;
              padding-top: 14px;
            }

            @media print {
              html, body {
                background: #ffffff !important;
                min-height: auto;
                height: 100%;
              }
              .standee-container {
                padding: 0;
              }
              .standee-card {
                box-shadow: none !important;
                border: 3.5px solid #27306f !important;
                page-break-inside: avoid;
              }
            }
          </style>
        </head>
        <body>
          <div class="standee-container">
            <div class="standee-card">
              <div class="brand-badge">⚡ AsmitA Secure Access</div>
              <h1 class="property-title">${resolvedPropertyName || "Secure Campus"}</h1>
              <p class="gate-info">${selectedGateName} • Terminal #${selectedGate}</p>
              <div class="category-tag">${typeTitle}</div>

              <div class="qr-frame">
                <img class="qr-img" src="${qrDataUrl}" alt="Scan QR to Check In" />
                <div class="qr-center-badge">
                  <img class="qr-center-logo" src="${origin}/splash_logo.png" alt="AsmitA Logo" />
                </div>
              </div>

              <p class="scan-hint">📷 Point smartphone camera or scanner to register</p>

              <div class="divider"></div>

              <div class="steps-grid">
                <div class="step-item">
                  <div class="step-num">1</div>
                  <div class="step-title">Scan QR</div>
                  <div class="step-text">Open phone camera or scanner</div>
                </div>
                <div class="step-item">
                  <div class="step-num">2</div>
                  <div class="step-title">Verify OTP</div>
                  <div class="step-text">Enter phone & confirm OTP</div>
                </div>
                <div class="step-item">
                  <div class="step-num">3</div>
                  <div class="step-title">Get Pass</div>
                  <div class="step-text">Show digital gate badge</div>
                </div>
              </div>

              <div class="footer-note">POWERED BY ASMITA SMART OPERATIONS PLATFORM</div>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 400);
            };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className={styles.header}>
          <div className={styles.titleWrap}>
            <div className={styles.iconCircle}>
              <QrCode size={22} />
            </div>
            <div>
              <h3 className={styles.title}>Generate Walk-in QR Code</h3>
              <p className={styles.subtitle}>
                {resolvedPropertyName || "Property Access"} (Property ID: #{societyId})
              </p>
            </div>
          </div>
          <button className={styles.closeBtn} onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <div className={styles.body}>
          {/* Config Controls */}
          <div className={styles.formGrid}>
            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Select Gate / Entrance</label>
              <SelectDropdown
                options={gateOptions}
                value={selectedGate}
                onChange={(e, val) => setSelectedGate(String(val || "1"))}
                isSearchable={gateOptions.length > 5}
                isClearable={false}
                placeholder="Select gate..."
              />
            </div>

            <div className={styles.fieldGroup}>
              <label className={styles.fieldLabel}>Entry Category</label>
              <SelectDropdown
                options={categoryOptions}
                value={selectedType}
                onChange={(e, val) => setSelectedType(String(val || "ALL"))}
                isSearchable={false}
                isClearable={false}
                placeholder="Select category..."
              />
            </div>
          </div>

          {/* Live Standee Preview Card */}
          <div className={styles.posterPreview}>
            <div className={styles.posterBrand}>
              <Shield size={12} />
              <span>AsmitA Walk-in Pass Standee</span>
            </div>
            <h4 className={styles.posterTitle}>{propertyName}</h4>
            <div className={styles.posterGateBadge}>
              <MapPin size={14} />
              <span>{selectedGateName}</span>
              <span>•</span>
              <span style={{ color: "#059669" }}>
                {selectedType === "ALL" ? "All Categories" : selectedTypeObj.label}
              </span>
            </div>

            <div className={styles.qrContainer}>
              <QRCodeCanvas
                id="standee-qr-canvas"
                value={targetUrl}
                size={520}
                level="H"
                marginSize={1}
                imageSettings={{
                  src: "/splash_logo.png",
                  height: 68,
                  width: 68,
                  excavate: true,
                }}
                style={{ width: "200px", height: "200px", display: "block" }}
              />
            </div>

            <p className={styles.instructions}>
              Visitors scan this QR on arrival to verify OTP and register instantly.
              {selectedType !== "ALL" && (
                <span style={{ display: "block", marginTop: "4px", fontWeight: "600", color: "#1e3a8a" }}>
                  Category is pre-locked to {selectedTypeObj.label}.
                </span>
              )}
            </p>
          </div>

          {/* Copyable Check-in URL */}
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>Encoded Terminal URL</label>
            <div className={styles.urlBox}>
              <span className={styles.urlText} title={targetUrl}>
                {targetUrl}
              </span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
              >
                {copied ? <Check size={14} color="#059669" /> : <Copy size={14} />}
                <span>{copied ? "Copied" : "Copy"}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Close
          </button>
          <a
            href={targetUrl}
            target="_blank"
            rel="noreferrer"
            className={styles.cancelBtn}
            style={{ textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" }}
          >
            <ExternalLink size={14} /> Open Link
          </a>
          <button
            type="button"
            className={styles.printBtn}
            onClick={handlePrintPoster}
          >
            <Printer size={16} /> Print Standee Poster
          </button>
        </div>
      </div>
    </div>
  );
}
